import type { ReactNode } from "react";
import { Footer } from "@/components/landing/footer";
import { Nav } from "@/components/landing/nav";

export type LegalSection = {
	id: string;
	heading: string;
	body: ReactNode;
};

type LegalPageProps = {
	eyebrow: string;
	title: string;
	summary: string;
	lastUpdated: string;
	sections: LegalSection[];
};

export function LegalPage({
	eyebrow,
	title,
	summary,
	lastUpdated,
	sections,
}: LegalPageProps) {
	return (
		<div className="overflow-x-hidden bg-surface">
			<Nav />
			<main className="pt-24">
				{/* Header */}
				<header className="bg-white px-4 pt-12 pb-16">
					<div className="max-w-6xl mx-auto">
						<div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16 items-end">
							<div>
								<span className="inline-flex items-center rounded-full bg-info px-4 py-1.5 text-primary text-xs font-bold uppercase tracking-[0.14em]">
									{eyebrow}
								</span>
								<h1 className="mt-6 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] text-dark">
									{title}
								</h1>
								<p className="mt-5 text-lg text-slate leading-relaxed max-w-[52ch]">
									{summary}
								</p>
							</div>
							<p className="text-sm text-slate lg:text-right lg:pb-2">
								Last updated{" "}
								<time className="font-semibold text-dark">{lastUpdated}</time>
							</p>
						</div>
					</div>
				</header>

				{/* Body */}
				<div className="px-4 py-16 lg:py-20">
					<div className="max-w-6xl mx-auto grid lg:grid-cols-[240px_1fr] gap-10 lg:gap-16">
						{/* Contents */}
						<nav
							aria-label="On this page"
							className="hidden lg:block lg:sticky lg:top-28 lg:self-start"
						>
							<h2 className="text-xs font-bold uppercase tracking-[0.14em] text-slate">
								On this page
							</h2>
							<ul className="mt-4 space-y-1 border-l border-border-subtle">
								{sections.map((section) => (
									<li key={section.id}>
										<a
											href={`#${section.id}`}
											className="block -ml-px border-l-2 border-transparent pl-4 py-1.5 text-sm text-slate hover:border-primary hover:text-primary transition-colors"
										>
											{section.heading}
										</a>
									</li>
								))}
							</ul>
						</nav>

						<article className="max-w-[68ch] rounded-2xl border border-border-subtle bg-white px-6 py-8 shadow-soft sm:px-10 sm:py-10">
							{sections.map((section, index) => (
								<section
									key={section.id}
									id={section.id}
									className="scroll-mt-28 border-border-subtle pt-8 first:pt-0 [&:not(:first-child)]:border-t"
								>
									<h2 className="text-2xl font-extrabold tracking-tight text-dark">
										<span className="text-primary tabular-nums">
											{String(index + 1).padStart(2, "0")}
										</span>
										<span className="ml-3">{section.heading}</span>
									</h2>
									<div className="mt-4 pb-8 space-y-4 text-slate leading-relaxed [&_a]:text-primary [&_a]:font-semibold [&_a:hover]:underline [&_li]:pl-1 [&_strong]:text-dark [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5">
										{section.body}
									</div>
								</section>
							))}

							<aside className="mt-10 rounded-xl bg-info px-6 py-6">
								<h2 className="font-extrabold text-dark">Questions?</h2>
								<p className="mt-2 text-slate leading-relaxed">
									Email{" "}
									<a
										href="mailto:support@myproxi.ng"
										className="text-primary font-semibold hover:underline"
									>
										support@myproxi.ng
									</a>{" "}
									and our team will get back to you.
								</p>
							</aside>
						</article>
					</div>
				</div>
			</main>
			<Footer />
		</div>
	);
}

/** Registered-entity details, shown identically on every legal page. */
export function CompanyDetails() {
	return (
		<ul className="not-prose space-y-2">
			<li>
				<strong>Registered name:</strong> myProxi Digital Solutions Ltd.
			</li>
			<li>
				<strong>RC number:</strong> 9083450 (Corporate Affairs Commission,
				incorporated 17 December 2025)
			</li>
			<li>
				<strong>Registered address:</strong> 11 Olufemi Apara Close, Gbagada,
				Lagos State, Nigeria
			</li>
			<li>
				<strong>Email:</strong>{" "}
				<a href="mailto:info@myproxi.ng">info@myproxi.ng</a>
			</li>
		</ul>
	);
}
